import {
  Button,
  Callout,
  Classes,
  H3,
  H5,
  Icon,
  Intent,
  Spinner,
} from '@blueprintjs/core';
import * as React from 'react';
import { usePortalConnections } from 'vet-bones/utils/portal';

import { ConnectionMenu } from 'src/containers/ConnectionMenu/ConnectionMenu';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { connectToStardog } from 'src/store/connections/connectToStardog';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';

type DialogConnectionTileProps = {
  hideSwitchConnections?: boolean;
  cardClassName: string;
  handleProceedWithConnection: () => void;
  proceedButtonTestId?: string;
  switchConnectionTestId?: string;
  title?: string;
};

export const DialogConnectionTile: React.VFC<DialogConnectionTileProps> = ({
  hideSwitchConnections = false,
  cardClassName,
  handleProceedWithConnection,
  title,
  proceedButtonTestId,
  switchConnectionTestId,
}) => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const alreadyProceeded = React.useRef(false);

  const defaultConnection = useDefaultPortalConnection();

  const portalConnections = usePortalConnections();
  useSetupDefaultPortalConnection({ connect: false });

  const proceed = React.useCallback(() => {
    // In case someone clicks the back button, don't autoProceed again
    if (!alreadyProceeded.current) {
      alreadyProceeded.current = true;
      handleProceedWithConnection();
    }
  }, [handleProceedWithConnection]);

  const [shouldProceed, setShouldProceed] = React.useState(false);
  React.useEffect(() => {
    if (shouldProceed && isConnected) {
      proceed();
    }
  }, [isConnected, proceed, shouldProceed]);

  const showSwitchConnections =
    portalConnections.data &&
    portalConnections.data.length &&
    portalConnections.data.some((c) => c.id !== defaultConnection?.id);

  return (
    <Callout className={cardClassName} intent={Intent.NONE}>
      {title ? <H3>{title}</H3> : null}
      <Icon
        color={isConnected ? colors.darkIcon : undefined}
        icon={icons.connection}
        iconSize={24}
      />
      {!connection || isConnecting ? (
        <Spinner />
      ) : (
        <>
          {showSwitchConnections ? <H5>{connection.name}</H5> : null}
          <span>{connection.endpoint}</span>
          {connection.username ? (
            <span>
              {copy.components.dialogConnectionTile.connection.username(
                connection.username
              )}
            </span>
          ) : null}
          <Button
            data-testid={proceedButtonTestId}
            intent={Intent.PRIMARY}
            large
            onClick={() => {
              setShouldProceed(true);
              dispatch(
                connectToStardog({ connection, shouldNotNotifySuccess: true })
              );
            }}
            text={copy.components.dialogConnectionTile.connection.proceedButton}
          />
          {showSwitchConnections && !hideSwitchConnections ? (
            <div className={Classes.TEXT_SMALL}>
              <span>
                {
                  copy.components.dialogConnectionTile.connection
                    .otherConnection
                }
              </span>
              <ConnectionMenu connect={false} disabled={isConnecting}>
                <a
                  data-testid={switchConnectionTestId}
                  role="link"
                  tabIndex={0}
                >
                  {copy.components.dialogConnectionTile.connection.selectHere}
                </a>
              </ConnectionMenu>
            </div>
          ) : null}
        </>
      )}
    </Callout>
  );
};
